import { FC, useState, useCallback, useMemo, useEffect } from "react";
import { Box, Popover, Stack, Typography, styled } from "@mui/material";
import { background, divider } from "@/theme/colors";
import { IconCheck } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import {
  TExamStepsFilterFormExtended,
  defaultExamStepsFilterValues,
  isEmptyExamStepsFilterValue,
  normalizeExamStepsDateFields,
  normalizeExamStepsLegacyFields,
  stripEmptyExamStepsFilterFields,
} from "@/features/exam-steps/components/filter/exam-steps-filter-shared";
import { ExamStepsFilterFormFields } from "@/features/exam-steps/components/filter/ExamStepsFilterFormFields";
import { ExamStepsSaveConditionModal } from "@/features/exam-steps/components/filter/ExamStepsSaveConditionModal";
import { useExamStepsFilterMasterData } from "@/features/exam-steps/components/filter/use-exam-steps-filter-master-data";
import { useCreateSavedCondition } from "@/api/services/main/saved-condition";
import { toast } from "@/utils/toast";
import { TOption } from "@/types/common";

const SAVED_CONDITION_PAGE_TYPE = "exam_steps";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApplyFilters?: (filters: TExamStepsFilterFormExtended) => void;
  initialFilters?: Partial<TExamStepsFilterFormExtended>;
  industryId?: number;
};

export const ExamStepsFilterModal: FC<Props> = ({ anchorEl, onClose, onApplyFilters, initialFilters, industryId }) => {
  const open = Boolean(anchorEl);

  const [filterValues, setFilterValues] = useState<TExamStepsFilterFormExtended>({
    ...defaultExamStepsFilterValues,
    ...(normalizeExamStepsLegacyFields(
      normalizeExamStepsDateFields(initialFilters ?? {}) as Partial<Record<string, unknown>>,
    ) as Partial<TExamStepsFilterFormExtended>),
  });

  // Re-sync local state when the popover reopens so the latest applied values are reflected.
  useEffect(() => {
    if (!open) return;
    setFilterValues({
      ...defaultExamStepsFilterValues,
      ...(normalizeExamStepsLegacyFields(
        normalizeExamStepsDateFields(initialFilters ?? {}) as Partial<Record<string, unknown>>,
      ) as Partial<TExamStepsFilterFormExtended>),
    });
  }, [open, initialFilters]);

  const handleFilterChange = useCallback(
    (field: keyof TExamStepsFilterFormExtended, value: unknown) => setFilterValues((prev) => ({ ...prev, [field]: value })),
    [],
  );

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const createSavedCondition = useCreateSavedCondition();
  const { options: masterOptions } = useExamStepsFilterMasterData();

  // Resolve multi-select IDs to comma-joined labels so the save-modal preview shows readable names.
  const resolvedLabels = useMemo<Partial<Record<keyof TExamStepsFilterFormExtended, string>>>(() => {
    const labelsFor = (options: TOption[], value: string[]): string | undefined => {
      if (!value || value.length === 0) return undefined;
      const labels = value.map((v) => options.find((o) => String(o.value) === String(v))?.label).filter(Boolean) as string[];
      return labels.length > 0 ? labels.join("、") : undefined;
    };
    const mapping: { key: keyof TExamStepsFilterFormExtended; options: TOption[] }[] = [
      { key: "rsoCompanyIds", options: masterOptions.organization },
      { key: "nationalityIds", options: masterOptions.nationality },
      { key: "procedureStatuses", options: masterOptions.procedureStatus },
    ];
    const out: Partial<Record<keyof TExamStepsFilterFormExtended, string>> = {};
    for (const { key, options } of mapping) {
      const label = labelsFor(options, filterValues[key] as string[]);
      if (label) out[key] = label;
    }
    return out;
  }, [filterValues, masterOptions]);

  const hasFilterData = useMemo(
    () => Object.values(filterValues).some((v) => !isEmptyExamStepsFilterValue(v)),
    [filterValues],
  );

  const handleSaveCondition = useCallback(
    async (title: string) => {
      // Let the error propagate so ExamStepsSaveConditionModal can showError + keep itself open.
      const strippedFilters = stripEmptyExamStepsFilterFields(filterValues);
      await createSavedCondition.mutateAsync({
        pageType: SAVED_CONDITION_PAGE_TYPE,
        title,
        filters: industryId ? { [industryId]: strippedFilters } : strippedFilters,
      });
      toast.success("条件を保存しました。");
    },
    [createSavedCondition, filterValues, industryId],
  );

  const handleClearConfirm = useCallback(() => {
    setFilterValues(defaultExamStepsFilterValues);
    setIsClearConfirmOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    onApplyFilters?.(filterValues);
    onClose();
  }, [filterValues, onApplyFilters, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPaper-root": {
          mt: 1,
          p: 0,
          borderRadius: "8px",
          boxShadow: "0px 8px 24px 0px #00000026",
        },
      }}
    >
      <PopoverContainer>
        <ContentContainer>
          <PanelHeader>
            <Typography variant="sub16Bold">フィルタ</Typography>
          </PanelHeader>

          <ScrollableContent>
            <ExamStepsFilterFormFields filterValues={filterValues} onFilterChange={handleFilterChange} />
          </ScrollableContent>
        </ContentContainer>

        <FooterContainer>
          <Stack flexDirection="row" gap="16px">
            <Button variant="ghost" onClick={onClose} size="sm">
              キャンセル
            </Button>
            <Button variant="text" onClick={() => hasFilterData && setIsClearConfirmOpen(true)} size="sm" disabled={!hasFilterData}>
              クリア
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px">
            {/* 一時的に「設定条件を保存」ボタンを非表示にしている。
            <Button variant="outline" size="sm" startIcon={<IconSave />} onClick={() => setIsSaveModalOpen(true)} disabled={!hasFilterData}>
              設定条件を保存
            </Button>
            */}
            <Button onClick={handleApply} size="sm" startIcon={<IconCheck width={20} height={20} />}>
              条件を設定
            </Button>
          </Stack>
        </FooterContainer>
      </PopoverContainer>

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearConfirm}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />

      <ExamStepsSaveConditionModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleSaveCondition}
        filterValues={filterValues}
        resolvedLabels={resolvedLabels}
      />
    </Popover>
  );
};

const PopoverContainer = styled(Box)`
  width: 780px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: ${background.white};
  border-radius: 8px;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PanelHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid ${divider.middle};
`;

const ScrollableContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px 24px;
`;

const FooterContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid ${divider.middle};
`;
